import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useRef, useState } from "react";
import Modal from 'react-modal';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../../../constants/command-status";
import { TYPE_ENTRY_AND_EXIT, TYPE_ENTRY_ONLY } from "../../../../constants/install-device";
import { TASK_STATUS_COMPLETED, TASK_STATUS_ERROR, TASK_STATUS_FAILED, TYPE_REQ_INSTALL, WS_COUNT_STATES } from "../../../../constants/ws-constants";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../../../../contexts/websocket/IcaWebsocketContext";
import { Controller } from "../../../../models/Controller";
import WizardSideBarStep from "../../../../models/WizardSideBarStep";
import { useGlobalState } from "../../../../Providers";
import { installController } from "../../../../services/installService";
import { getErrorMessage } from "../../../../services/utilities/ServiceError";
import { IAppState } from "../../../../store/types";
import { useDialog } from "../../../dialog/DialogContext";
import ProgressModal from "../../../progress/ProgressModal";
import WizardTwo from "../../../wizard/wizardTwo";
import DetailCloudController from "../installCloudController/DetailCloudController";
import DoorCreation from "./DoorCreation";
import InstallPeripheral from "./InstallPeripheral";
import SelectController from "./SelectController";
import SelectIPDevice from "./SelectIPDevice";
import Summary from "./Summary";
import { NETWORK_MODE } from "../../../../constants/network-constants";
import { getControllerById } from "../../../../services/controllerService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSync, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

interface InstallMasterControllerModalProps {
    isOpen: boolean;
    onClose: () => void;
    postInstall: () => void;
}

const InstallMasterControllerModal: FC<InstallMasterControllerModalProps> = ({ isOpen, onClose, postInstall }) => {
    const { translate } = useLocale();
    const GlobalContext = useGlobalState();
    const isTlsEnabled = GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.CLOUD || GlobalContext?.buildInfo.tlsMode === NETWORK_MODE.LAN;
    const [currentStep, setCurrentStep] = useState(0);
    const siteId = useSelector((state: IAppState) => state.site.site.value)
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
    const [found, setFound] = useState<number>(0);
    const [installed, setInstalled] = useState<number>(0);
    const [progress, setProgress] = useState<number>(0);
    const [hasPwdsChanged, setPwdChanged] = useState<boolean>(false);
    // Add new state to track websocket errors
    const hasWebSocketError = useRef(false);
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();
    const initialState: Controller = {
        id: 0,
        name: '',
        autoCreateDoorsType: TYPE_ENTRY_ONLY,
        autoCreateDoors: true,
        selectedPeripherals: [],
        selectIPDevices: [],
        // detectDownstreamIpDevices: false,
        // detectPeripheralsDirectly: false, // not yet supported
    };
    const [selectedDevice, setSelectedDevice] = useState<Controller>(initialState);

    /**
    * This is here to make sure that the progress modal
    * is closed when the install modal is open and reset
    * the state to hide install modal when progress modal
    * is shown.
    */
    useEffect(() => {
        if(isOpen) {
            setIsProgressModalOpen(false);
            hasWebSocketError.current = false;  // Reset using ref
        }
    }, [isOpen])

    const onNext = (e) => {
        setCurrentStep(currentStep + 1);
    };

    const onBack = (e) => {
        setCurrentStep(currentStep - 1);
    };

    const onInstallWizardClose = () => {
        icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        setCurrentStep(0);
        setSelectedDevice(initialState);
        setIsProgressModalOpen(false);
        setProgress(0);
        setFound(0);
        setInstalled(0);
        onClose();
    }

    const installDevice = () => {
        onClose();
        hasWebSocketError.current = false; // Reset using ref

        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage)
        }

        installController({ ...selectedDevice, siteId: siteId }, user.id, true)
            .then(() => {
                // Only show progress modal if we haven't received a websocket error
                if (!hasWebSocketError.current) {
                    setIsProgressModalOpen(true);
                }
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TASKS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
            })
    }

    const onPremStepContent: ReactElement[] = [
        <SelectController key={`installStep-0`}
            selectedDevice={selectedDevice}
            setSelectedDevice={setSelectedDevice}
            onNext={onNext}
            title={translate.byKey(TranslationKeys.selectADeviceToInstall)} />,
        <InstallPeripheral key={'installStep-1'}
            selectedDevice={selectedDevice}
            onNext={onNext}
            onBack={onBack}
            setSelectedDevice={setSelectedDevice} />,
        <SelectIPDevice key={`installStep-2`}
            onNext={onNext}
            onBack={onBack}
            setSelectedDevice={setSelectedDevice}
            selectedDevice={selectedDevice} />,
        <DoorCreation key={`installStep-3`}
            setSelectedDevice={setSelectedDevice}
            onBack={onBack}
            onNext={onNext} />,
        <Summary key={`installStep-4`}
            onBack={onBack} />
    ];

    const onPremNewSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.selectController),
            description: selectedDevice.fixedAddress,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.selectPeripherals),
            description: selectedDevice.selectedPeripherals ? selectedDevice.selectedPeripherals?.length + ` ${translate.byKey(TranslationKeys.peripherals)}` : translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.selectIpDevices),
            description: selectedDevice.selectIPDevices ? selectedDevice.selectIPDevices?.length + ` ${translate.byKey(TranslationKeys.IPDevices)}` : translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.doorCreation),
            description: selectedDevice.autoCreateDoorsType === TYPE_ENTRY_ONLY ?
                translate.byKey(TranslationKeys.entryReaderOnly) :
                selectedDevice.autoCreateDoorsType === TYPE_ENTRY_AND_EXIT ?
                    translate.byKey(TranslationKeys.exitAndEntryReaders) : currentStep === 3 ?
                        translate.byKey(TranslationKeys.notApplicable) :
                        translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ]

    const cloudStepContent: ReactElement[] = [
        <SelectController
            key="installStep-0"
            selectedDevice={selectedDevice}
            setSelectedDevice={setSelectedDevice}
            onNext={onNext}
            title={translate.byKey(TranslationKeys.selectADeviceToInstall)}
        />,
        <DetailCloudController
            key="installStep-1"
            setSelectedDevice={setSelectedDevice}
            device={selectedDevice}
            onNext={onNext}
            onBack={onBack}
        />,
        (selectedDevice.detectPeripheralsDirectly) && (<InstallPeripheral
            key = "installStep-2"
            selectedDevice={selectedDevice}
            onNext={onNext}
            onBack={onBack}
            setSelectedDevice={setSelectedDevice}
        />),
        (selectedDevice.detectDownstreamIpDevices) && (
            <SelectIPDevice
                key="installStep-3"
                onNext={onNext}
                onBack={onBack}
                setSelectedDevice={setSelectedDevice}
                selectedDevice={selectedDevice}
            />
        ),
        <DoorCreation
            key="installStep-4"
            setSelectedDevice={setSelectedDevice}
            onBack={onBack}
            onNext={onNext}
        />,
        <Summary key={`installStep-4`}
            onBack={onBack} />
    ].filter(Boolean) as ReactElement[];

    const cloudNewSteps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.selectController),
            description: selectedDevice.fixedAddress,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.controllerDetails),
            description: (selectedDevice.macAddress || translate.byKey(TranslationKeys.pending)),
            substeps: []
        },
        (selectedDevice.detectPeripheralsDirectly) &&(
            {
                title: translate.byKey(TranslationKeys.selectPeripherals),
                description: selectedDevice.selectedPeripherals ? selectedDevice.selectedPeripherals?.length + ` ${translate.byKey(TranslationKeys.peripherals)}` : translate.byKey(TranslationKeys.pending),
                substeps: []
            }
        ),
        (selectedDevice.detectDownstreamIpDevices) && (
            {
                title: translate.byKey(TranslationKeys.selectIpDevices),
                description: selectedDevice.selectIPDevices ? selectedDevice.selectIPDevices?.length + ` ${translate.byKey(TranslationKeys.IPDevices)}` : translate.byKey(TranslationKeys.pending),
                substeps: []
            }
        ),
        {
            title: translate.byKey(TranslationKeys.doorCreation),
            description: selectedDevice.autoCreateDoorsType === TYPE_ENTRY_ONLY ?
                translate.byKey(TranslationKeys.entryReaderOnly) :
                selectedDevice.autoCreateDoorsType === TYPE_ENTRY_AND_EXIT ?
                    translate.byKey(TranslationKeys.exitAndEntryReaders) : currentStep === 3 ?
                        translate.byKey(TranslationKeys.notApplicable) :
                        translate.byKey(TranslationKeys.pending),
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        },
    ].filter(Boolean) as WizardSideBarStep[];

    const onMessage = (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            let status: number = 0, progress: number = 0, countType: number = 0, found: number = 0, installed: number = 0, pwdChanged: number = 0;
            let controllerId = 0;
            if (response.command === MONITORING_COMMAND.MONITORING_TASKS || response.command === MONITORING_COMMAND.MONITORING_QUEUE) {
                if (response.states) {
                    response.states.map((state) => {
                        if (state.attributes.taskStatus && state.attributes.taskStatus.requestMessageType === TYPE_REQ_INSTALL) {
                            progress = state.attributes.taskStatus.message ? state.attributes.taskStatus.message : 0;
                            status = state.attributes.taskStatus.status;
                            controllerId= state.attributes.taskStatus.domainId;
                            setProgress(progress);
                        }
                        else if (state.attributes.countStatus) {
                            countType = state.attributes.countStatus.countType;

                            if (countType === WS_COUNT_STATES.COUNT_STATUS_FOUND) {
                                found = state.attributes.countStatus.count;
                                setFound(found)
                            }
                            else if (countType === WS_COUNT_STATES.COUNT_STATUS_INSTALLED) {
                                installed = state.attributes.countStatus.count;
                                setInstalled(installed)
                            }
                            else if (countType === WS_COUNT_STATES.COUNT_STATUS_PWD_AUTO_UPDATED) {
                                pwdChanged = state.attributes.countStatus.count;
                                setPwdChanged(pwdChanged > 0);
                            }
                        }
                    })
                }

                if (status === TASK_STATUS_COMPLETED) {
                    onInstallWizardClose();
                    postInstall();
                    toast.success(translate.byKey(TranslationKeys.controllerSaved));
                }
                else if (status === TASK_STATUS_ERROR || status === TASK_STATUS_FAILED) {
                    hasWebSocketError.current = true;  // Set using ref
                    onInstallWizardClose();
                    if (response.states && response.states.length > 0 && response.states[0].attributes.taskStatus) {
                        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: response.states[0].attributes.taskStatus.message });
                    }
                    else {
                        dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: translate.byKey(TranslationKeys.controllerInstallFailure) });
                    }
                }
            }
        }
    }

    return <>
        <Modal
            isOpen={isOpen}
            ariaHideApp={false}
            overlayClassName={`flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white`}
            className={`bg-white border-none m-auto shadow select-none outline-none w-content `}>
            <Formik
                enableReinitialize={true}
                initialValues={selectedDevice}
                onSubmit={installDevice}>
                <Form>
                    <WizardTwo
                        name={translate.byKey(TranslationKeys.installMasterController)}
                        step={currentStep}
                        onClose={onInstallWizardClose}
                        sidebarSteps={isTlsEnabled ? cloudNewSteps : onPremNewSteps}
                        stepContent={isTlsEnabled ? cloudStepContent : onPremStepContent} />
                </Form>
            </Formik>
        </Modal>
        <ProgressModal isOpen={isProgressModalOpen} title={translate.byKey(TranslationKeys.installMasterController)}>
            <div className="flex flex-col py-3 pr-4">
                <label className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                    {translate.byKey(TranslationKeys.found)} {found.toString()}
                </label>
                <label className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faCheck} className="text-green-600" />
                    {translate.byKey(TranslationKeys.installed)}: {installed.toString()}
                </label>
                {progress.toString() === '100' ?
                    <label className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faSync} className="text-green-600" />
                        {translate.byKey(TranslationKeys.updatingHardwareSettings)}
                    </label> :
                    <label className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faSync} className="text-blue-600" />
                        {translate.byKey(TranslationKeys.completed)} {progress.toString()}%
                    </label>
                }
                {hasPwdsChanged &&
                <div className="flex items-start gap-2">
                    <label >
                        <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600" />
                    </label>
                    <label className="break-words whitespace-normal max-w-xs">
                        {translate.byKey(TranslationKeys.passwordChangedMessage)}
                    </label>
                </div>
                }
            </div>
        </ProgressModal>
    </>
}

export default InstallMasterControllerModal;
